import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const columns = [
  { key: "todo", label: "To Do", color: "text-white/60" },
  { key: "in_progress", label: "In Progress", color: "text-blue-400" },
  { key: "review", label: "Review", color: "text-purple-400" },
  { key: "done", label: "Done", color: "text-emerald-400" },
];

const priorityStyles = {
  low: "bg-white/5 text-white/60",
  medium: "bg-blue-500/10 text-blue-400",
  high: "bg-amber-500/10 text-amber-400",
  urgent: "bg-red-500/10 text-red-400",
};

const empty = { title: "", description: "", priority: "medium", status: "todo", deadline: "" };

export default function Tasks() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/tasks").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const move = async (t, status) => {
    await api.put(`/tasks/${t.id}`, { ...t, status });
    load();
  };

  const del = async (id) => {
    await api.delete(`/tasks/${id}`); load();
  };

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    await api.post("/tasks", form);
    toast.success("Added"); setOpen(false); setForm(empty); load();
  };

  return (
    <>
      <PageHeader title="Tasks" subtitle={`${list.filter(t => t.status !== "done").length} open`}
        actions={<button data-testid="new-task-btn" onClick={() => setOpen(true)} className="bg-white text-black text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1.5 hover:bg-white/90 active:scale-[0.98]"><Plus size={14} weight="bold" /> New Task</button>}
      />
      <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {columns.map(col => {
          const items = list.filter(t => t.status === col.key);
          return (
            <div key={col.key} data-testid={`col-${col.key}`} className="hairline rounded-lg bg-[#0F0F0F] flex flex-col min-h-[400px]">
              <div className="p-3 hairline-b flex items-center justify-between">
                <div className={`text-[10px] uppercase tracking-widest font-semibold ${col.color}`}>{col.label}</div>
                <div className="mono text-[10px] text-white/40">{items.length}</div>
              </div>
              <div className="p-2 space-y-2 flex-1">
                {items.map(t => (
                  <div key={t.id} data-testid={`task-${t.id}`} className="hairline rounded-md p-3 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                    <div className="text-sm font-medium">{t.title}</div>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-widest ${priorityStyles[t.priority]}`}>{t.priority}</span>
                      {t.deadline && <span className="text-[10px] mono text-white/50">{fmtDate(t.deadline)}</span>}
                    </div>
                    <div className="flex items-center gap-1 mt-3 pt-2 border-t border-white/5">
                      <select value={t.status} onChange={e => move(t, e.target.value)} className="text-[10px] bg-transparent border border-white/10 rounded px-2 py-0.5 flex-1 focus:outline-none">
                        {columns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                      <button onClick={() => del(t.id)} className="text-white/40 hover:text-red-400 p-1"><Trash size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10">
          <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Title*</label><input data-testid="task-input-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Description</label><textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows="2" className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] uppercase tracking-widest text-white/40">Priority</label>
                <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm">
                  {["low", "medium", "high", "urgent"].map(x => <option key={x}>{x}</option>)}
                </select>
              </div>
              <div><label className="text-[10px] uppercase tracking-widest text-white/40">Deadline</label><input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm" /></div>
            </div>
          </div>
          <DialogFooter><button data-testid="save-task-btn" onClick={save} className="bg-white text-black text-xs font-semibold px-4 py-2 rounded-md hover:bg-white/90">Save</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
